import { FC } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useMutation } from "react-query";
import CreateIcon from "@mui/icons-material/Create";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { DeleteJobTitle } from "./DeleteJobTitle";
import { useJobsValue } from "../hooks/useJobsValue";
import { fetchDisableJobs } from "../miscellaneous/fetchDisableJobs";
import { ButtonA } from "../../common/components/ButtonA";
import { EditButton } from "../../common/components/EditButton";
import { useSelectTableRow } from "../../common/hooks/useSelectTableRow";
import { TableHeadCheckbox } from "../../common/components/TableHeadCheckbox";
import { TableCellCheckbox } from "../../common/components/TableCellCheckbox";
import { TableFooterCommon } from "../../common/components/TableFooterCommon";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";

type JobTitleTableProps = {
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
};

const tableCellHeadStyle = {
  fontWeight: 600,
};

export const JobsTable: FC<JobTitleTableProps> = ({
  page,
  rowsPerPage,
  totalRecords,
  handleChangePage,
  handleChangeRowsPerPage,
}) => {
  const jobs = useJobsValue();
  const { selected, handleSelectAllClick, isSelected, handleToggle } =
    useSelectTableRow(jobs);

  const navigate = useNavigate();

  const mutateDisableJobs = useMutation(fetchDisableJobs, {
    onSuccess: (res) => {
      if (res.status === 200) {
        navigate(0);
      }
    },
    onError: () => {},
  });

  const handleEditJobTitle = (id: string) => {
    navigate(`/update-job/${id}`);
  };

  const handleNavigateToCreate = () => {
    navigate("/create-job-title");
  };

  return (
    <div className="mt-4">
      <div className="flex flex-row justify-end gap-4 pb-3">
        <ButtonA
          name={"Create"}
          onHandleClick={handleNavigateToCreate}
          icon={<CreateIcon />}
        />
      </div>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
          <TableHead sx={{ backgroundColor: "#F5F4F4" }}>
            <TableRow>
              <TableCell sx={tableCellHeadStyle}>Company</TableCell>
              <TableCell sx={tableCellHeadStyle}>Organization</TableCell>
              <TableCell sx={tableCellHeadStyle}>Job Title</TableCell>
              <TableCell sx={tableCellHeadStyle}>Status</TableCell>
              <TableCell sx={tableCellHeadStyle} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {jobs.map((item: any) => {
              return (
                <TableRow key={item.id}>
                  <TableCell width={"200"}>{item.parentCompanyName}</TableCell>
                  <TableCell width={"200"}>{item.companyName}</TableCell>
                  <TableCell width={"200"}>{item.title}</TableCell>
                  <TableCell width={"160"}>
                    {item.isActive ? "Active" : "Inactive"}
                  </TableCell>
                  <TableCell width={"160"} align="center">
                    <div>
                      <EditButton
                        id={item.id}
                        onHandleEdit={() => {
                          handleEditJobTitle(item.id);
                        }}
                      />
                      <DeleteJobTitle id={item.id} info={item.title} />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>

          <TableFooterCommon
            data={jobs}
            rowsPerPage={rowsPerPage}
            page={page}
            onHandleChangePage={handleChangePage}
            onHandleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={totalRecords}
          />
        </Table>
      </TableContainer>
    </div>
  );
};
